import React, { useEffect, useRef, useState } from 'react';
import Select from "../../../components/select";
import TagSelect from "../../../components/tagSelect";
import Modal from "react-modal";
import axios from 'axios';
import { toast } from 'react-toastify';

import { uploadMediaFile } from '../../../utills/fileUpload';
import { convertToCloudFrontUrl, findSubPillarOption } from '../../../helper';
import SortablePeopleList from "../../../components/sortablePeople";
import TaxonomyStatus from "../../../components/taxonomyStatus";
import PublishResult from "../../../components/publishResult";
import { newIdempotencyKey } from "../../../helper/idempotency";
import { apiErrorMessage, apiBodyMessage, isTimeout } from "../../../helper/apiError";


const GuideSectionPopup = (props) => {
    const { 
      isAddGuideModalOpen, 
      isEditGuideModalOpen,  
      handleCloseModal, 
      postData = {}, 
      adminInfo, 
      onTagCreated,
      subPillarOptions = [],
      tagOptions = [],
      taxonomyState = "ready",
      onRetryTaxonomy,
      onSuccess 
    } = props;

    const initialPostValues = {
      title: "",
      type: "NonFiction",
      // Only the sub-pillar is chosen; the backend resolves its parent pillar.
      subPillarId: "",
      tags: [],
      media: [{ position: 1, image: null }],
      mediaType: "guide"
    };

    const [values, setValues] = useState(initialPostValues);
    const [editMediaError, setEditMediaError] = useState("");
    const [errors, setErrors] = useState({});

    const isFirstRender = useRef(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Submit waits for an upload, or the guide is saved without those images.
    const [isUploading, setIsUploading] = useState(false);
    // What the create saved, shown after the form closes (PublishResult).
    const [published, setPublished] = useState(null);
    // One key per guide being added: made when the Add form opens, kept across
    // retries, replaced only after a create succeeds.
    const idempotencyKey = useRef(null);
    useEffect(() => {
      if (isAddGuideModalOpen && !isEditGuideModalOpen && !idempotencyKey.current) {
        idempotencyKey.current = newIdempotencyKey();
      }
    }, [isAddGuideModalOpen, isEditGuideModalOpen]);
    const taxonomyReady = taxonomyState === "ready";

    useEffect(() => {
      if (isEditGuideModalOpen && postData) {
        setValues((prevValues) => ({
          ...prevValues,
          title: postData.title || "",
          type: postData.type || "NonFiction",
          subPillarId: postData.subPillar?.subPillarId || "",
          // Stored as {tagId, tagName}; react-select wants {value, label}.
          tags: Array.isArray(postData.tags)
            ? postData.tags.map((t) => ({ label: t.tagName, value: t.tagId }))
            : [],
          media: postData.carouselImages || [],
        }));
      } else if (!isEditGuideModalOpen && !isAddGuideModalOpen) {
        setValues(initialPostValues);
        setErrors({});
        setEditMediaError("");
      }
    }, [isEditGuideModalOpen, postData, isAddGuideModalOpen]);
    
    useEffect(() => {
      if (isFirstRender.current) {
          isFirstRender.current = false;
          addNewMediaRow(0);
      }
    }, [isFirstRender, values.media]);

    // `position` is stored on every carousel image and the app renders array
    // order, so the two must never disagree. Stamping it from the index on every
    // change is the only way to guarantee that.
    const withPositions = (media = []) =>
      media.map((item, i) => ({ ...item, position: i + 1 }));

    // Uploaded images have a stable key; an empty slot falls back to its index,
    // which is fine because an empty slot cannot be dragged anywhere useful.
    const mediaKey = (item, i) =>
      item?.image?.uploadedImageKey || item?.image?.uploadedImageUrl || `slot-${i}`;

    // Uploaded images as grid items, in carousel order. An empty slot is left
    // out — there is nothing to show and nothing to reorder.
    const mediaOptions = (values.media || [])
      .map((item, i) => ({ item, i }))
      .filter(({ item }) => item?.image?.uploadedImageUrl)
      .map(({ item, i }) => ({
        value: mediaKey(item, i),
        label: `Image ${i + 1}`,
        image: convertToCloudFrontUrl(item.image.uploadedImageUrl),
      }));

    // The grid hands back the order it wants — drag, position box or the
    // top/bottom buttons — so it is taken literally.
    const handleMediaReordered = (options) => {
      setValues((prevValues) => {
        const byKey = new Map(
          prevValues.media.map((item, i) => [mediaKey(item, i), item])
        );
        const next = (options || []).map((o) => byKey.get(o.value)).filter(Boolean);
        return { ...prevValues, media: withPositions(next) };
      });
    };


    const addNewMediaRow = (count = 1) => {
      if (values && values.media && values.media.length > 0) {
        const newMedia = [];

        for (let i = 0; i < count; i++) {
          newMedia.push({
            position: values.media.length + i + 1,
            image: null,
          });
        }
    
        setValues((prevValues) => ({
          ...prevValues,
          media: [...prevValues.media, ...newMedia],
        }));
      }
    };

    // The input is `multiple` and uploadMediaFile uploads every file picked, but
    // only the first was ever kept — the rest were uploaded to S3 and dropped on
    // the floor. Selecting ten images now adds ten, in the order they were
    // chosen, appended after the slot that was clicked.
    // The input is `multiple` and uploadMediaFile uploads every file picked, but
    // only the first was ever kept — the rest were uploaded to S3 and dropped on
    // the floor. Every file now becomes an entry, appended in the order chosen.
    //
    // Appended, never written into an existing slot: targeting one by index
    // overwrote whatever image was already there.
    const handleUploadMedia = async (event) => {
      if (!event?.target?.files?.length) return;
      setIsUploading(true);
      try {
        const result = await uploadMediaFile(event, true);

        if (typeof result === "string") {
          setEditMediaError(result);
          toast.error(result);
          return;
        }
        const uploaded = Array.isArray(result) ? result.map((r) => r?.data).filter(Boolean) : [];
        if (uploaded.length === 0) {
          toast.error("Nothing was uploaded. Please try again.");
          return;
        }

        setEditMediaError("");
        setErrors((prev) => ({ ...prev, media: undefined }));
        setValues((prevValues) => {
          // Empty slots go at the same time. The grid never showed them, and an
          // upload is the moment they stop being useful.
          const filled = (prevValues.media || []).filter(
            (item) => item?.image?.uploadedImageUrl
          );
          return {
            ...prevValues,
            media: withPositions([
              ...filled,
              ...uploaded.map((image) => ({ image })),
            ]),
          };
        });
      } catch (error) {
        console.error("GuidePopup handleUploadMedia catch error::", error);
        toast.error(apiErrorMessage(error, "The images could not be uploaded."));
      } finally {
        setIsUploading(false);
      }
    };

    const handleClose = () => {
      // Cancel ends this guide; the next Add is a new one with a new key.
      idempotencyKey.current = null;
      handleCloseModal();
      setValues(initialPostValues);
      setErrors({});
      setEditMediaError("");
    };


    // A sub-pillar that has since been deleted is no longer in the options, but
    // the guide is still tagged with it. Fall back to the name stored on the
    // guide so it displays and survives an unrelated edit — clearing it here
    // would strip the classification off every old guide the moment someone
    // fixed a typo. The backend resolves deleted ids for exactly this reason.
    const storedSubPillar = postData?.subPillar?.subPillarId
      ? {
          value: postData.subPillar.subPillarId,
          label: postData.subPillar.subPillarName || "(removed sub pillar)",
        }
      : null;

    const selectedSubPillar =
      findSubPillarOption(subPillarOptions, values.subPillarId) ||
      (storedSubPillar && storedSubPillar.value === values.subPillarId
        ? storedSubPillar
        : null);

    // Only a genuinely unrecognisable id gets dropped — one the options don't
    // know AND the guide doesn't carry. Guarded on options being loaded, so a
    // slow or failed fetch never wipes a good value.
    useEffect(() => {
      if (subPillarOptions.length === 0 || !values.subPillarId) return;
      if (!findSubPillarOption(subPillarOptions, values.subPillarId) &&
          storedSubPillar?.value !== values.subPillarId) {
        setValues((prev) => ({ ...prev, subPillarId: "" }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subPillarOptions, values.subPillarId]);

    const handleUserValuesUpdate = ({ name, value }) => {
      setValues((prev) => {
        const updateValues = { ...prev, [name]: value};

        return updateValues;
      })
    };

    const validateFields = (value) => {
      let newErrors = {};
    
      // Was "please add a category". The genre picker is gone: the backend
      // derives a guide's genres from its tags now, exactly as it does for
      // posts. So the tag is what has to be there — the mobile feed
      // personalises on genre, and a guide with no tags resolves to no genre
      // and would surface to nobody.
      if (!value.tags || value.tags.length === 0) {
        newErrors.tags = "Please add at least one tag";
      }
      if (!value.type || value.type.trim() === "") {
        newErrors.type = "Please add a type";
      }
    
      // One message rather than one per row. The rows are gone — images live in
      // a grid now — and an empty slot the admin never sees must not block
      // submit with an error that has nowhere to render.
      const uploadedImages = (value.media || []).filter(
        (item) => item?.image?.uploadedImageUrl
      );
      if (uploadedImages.length === 0) {
        newErrors.media = "Please upload at least one image";
      }
    
      return newErrors;
    };

    const handleAddAndUpdateValues = async () => {
      if (isSubmitting) return;
      if (isUploading) {
        toast.info("Wait for the upload to finish.");
        return;
      }
      // The sub-pillar and tag lists must be in: saving without them is how a
      // guide went out with no pillar because the dropdown was silently empty.
      if (!taxonomyReady) {
        toast.error("Pillars and tags haven't loaded — press Retry first.");
        return;
      }
      const newErrors = validateFields(values);
  
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error(Object.values(newErrors)[0]);
        return;
      };
      if (isEditGuideModalOpen && !postData?._id) {
        toast.error("This guide has no id, so it can't be updated. Close and open it again.");
        return;
      }
  
      setIsSubmitting(true);
      try {
        const headers = { Authorization: `Bearer ${adminInfo.token}` };
        const url = isEditGuideModalOpen
          ? `${process.env.REACT_APP_API_ROOT}/api/admin/post/update/${postData._id}`
          : `${process.env.REACT_APP_API_ROOT}/api/admin/post/add`;
        const method = isEditGuideModalOpen ? "put" : "post";
        // Empty slots never reach the server: the backend reads
        // `item.image.uploadedImageUrl` with no guard, so a slot the admin never
        // filled would throw there rather than fail here.
        const payload = {
          ...values,
          media: withPositions(
            (values.media || []).filter((item) => item?.image?.uploadedImageUrl)
          ),
        };
        // The key goes in the body, not a header: a custom header needs the
        // API's CORS to allow it, and the body works against any backend version.
        const body = !isEditGuideModalOpen && idempotencyKey.current ? { ...payload, idempotencyKey: idempotencyKey.current } : payload;
        const response = await axios[method](url, { values: body }, { headers, timeout: 60000 });
  
        if (response?.data?.status) {
          const wasEdit = isEditGuideModalOpen;
          setErrors({});
          onSuccess();
          handleCloseModal();
          setValues(initialPostValues);
          if (wasEdit) {
            toast.success("Guide updated successfully.");
          } else {
            idempotencyKey.current = null;
            // The create answers with the saved guide — show it, so nobody has
            // to go and find it in the list to check it.
            if (response.data.data?._id) setPublished({ post: response.data.data, duplicate: Boolean(response.data.duplicate) });
            else toast.success("Guide added successfully.");
          }
        } else {
          toast.error(apiBodyMessage(response?.data) || "The guide was not saved.");
        }
      } catch (error) {
        console.error("Error:", error);
        const timedOut = isTimeout(error);
        toast.error(
          timedOut
            ? "No answer from the server. Press Submit again — it won't create a second guide."
            : apiErrorMessage(error, "The guide was not saved.")
        );
      } finally {
        setIsSubmitting(false);
      }
    };

  
    return (
      <>
      {published && (
        <PublishResult post={published.post} duplicate={published.duplicate} onClose={() => setPublished(null)} />
      )}
      <Modal
          isOpen={isAddGuideModalOpen || isEditGuideModalOpen}
          onRequestClose={isSubmitting ? undefined : handleClose}
          className="modal_body_lg"
          overlayClassName="modal_overlay"
      >
          <div className="box_wrapper">
              <h4 className="text-center mb-3">{isEditGuideModalOpen ? "Edit Guide" : "Add Guide"}</h4>
              <TaxonomyStatus state={taxonomyState} onRetry={onRetryTaxonomy} />
              {/* _lg, not _sm: the small container caps at 800px, so on a dialog that
                  is 80% of the screen the form crowded into the left half and the rest
                  sat empty. */}
                  <div className="modal_content_lg">
                  <div className="mb-3">
                    <label className="label">Title:</label>
                    <input
                      className="form-control form-control-sm px-3 py-2 fw-medium"
                      type="text"
                      name="title"
                      placeholder="Short title to find this guide by..."
                      value={values.title || ""}
                      onChange={(e) => handleUserValuesUpdate({ name: e.target.name, value: e.target.value })}
                    />
                  </div>
                  <div className="d-flex gap-3 my-3">
                    <label className="label">Category Type:</label>
                      {["Fiction", "NonFiction"].map((option) => (
                        <div key={option} className="d-flex align-items-center gap-2">
                          <input
                            type="radio"
                            name="type"
                            value={option}
                            checked={values.type === option}
                            onChange={(e) => handleUserValuesUpdate({ name: e.target.name, value: e.target.value })}
                            style={{ width: "20px", height: "20px" }}
                          />
                          <label className="form-check-label">{option}</label>
                        </div>
                      ))}
                  </div>
                  <div className="mb-3">
                      <label className="label">Sub Pillar:</label>
                      <Select
                        name="subPillarId"
                        placeholder="Select a sub pillar..."
                        isClearable
                        value={selectedSubPillar}
                        onChange={(selected) => handleUserValuesUpdate({
                          name: "subPillarId",
                          value: selected?.value || ""
                        })}
                        options={subPillarOptions}
                      />
                      <small className="text-muted">
                        Grouped by pillar — picking a sub pillar files the guide under its pillar.
                      </small>
                  </div>
                  <div className="mb-3">
                      <label className="label">Tags:</label>
                      <TagSelect
                          value={values.tags || []}
                          onChange={(selected) => handleUserValuesUpdate({
                            name: "tags",
                            value: (selected || []).map((option) => ({ label: option.label, value: option.value })),
                          })}
                          options={tagOptions || []}
                          adminInfo={adminInfo}
                          onCreated={onTagCreated}
                      />
                      {errors.tags && <p className="error-message">{errors.tags}</p>}
                  </div>
                  <div className="mb-3">
                      <label className="label">Upload Media:</label>
                      {/* One input that takes many files at once, then a grid that owns order
                          and removal. The old stacked one-file-per-row list made a ten-image
                          carousel ten clicks and gave no way to reorder it. */}
                      <input
                          className="form-control py-3 w-100"
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={(e) => handleUploadMedia(e)}
                          onClick={(e) => { e.target.value = null }}
                      />
                      {isUploading && <p className="text-muted small mb-0">Uploading…</p>}
                      {editMediaError && <p className="error-message">{editMediaError}</p>}
                      <div className="mt-2">
                          <SortablePeopleList
                              items={mediaOptions}
                              onChange={handleMediaReordered}
                              thumbShape="cover"
                              variant="grid"
                              // The whole image, not a 2:3 crop — guide artwork is mostly type,
                              // and cropping cut the words off the top and bottom.
                              imageFit="contain"
                              cardMinWidth={240}
                              emptyMessage="No images yet — choose files above."
                          />
                      </div>
                      <small className="text-muted">
                          This is the order they appear in the carousel. Drag to reorder, or type
                          a position to move an image straight there.
                      </small>
                      {errors.media && <p className="error-message">{errors.media}</p>}
                      </div>
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3">
                <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={isSubmitting}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-dark"
                  onClick={handleAddAndUpdateValues}
                  disabled={isSubmitting || isUploading || !taxonomyReady}
                  title={!taxonomyReady ? "Waiting for pillars and tags to load" : undefined}
                >
                  {isSubmitting ? "Submitting ..." : isEditGuideModalOpen ? "Update" : "Submit"}
                </button>
              </div>
          </div>
      </Modal>
      </>
    );
  };

export default GuideSectionPopup
